import { useCallback, useEffect, useState } from 'react';

import { useOnMountEffect } from '~shared/hooks/use-on-mount-effect';

import { Button } from '@breezeventures/ui-kit/button';
import { Typography } from '@breezeventures/ui-kit/typography';
import { formatPhone } from '@breezeventures/utils/format-phone';
import { InputOtp } from '@breezeventures/web-ph-client-auth/input-otp';
import { ResendOtp } from '@breezeventures/web-ph-client-auth/resend-otp';
import { useAtomValue } from 'jotai';

import { OtpError } from './otp-error';
import { OtpErrorMessage } from './otp-error-message';

import { isWrongAnswerConfirmationError } from '../../api';
import { getOtpRequestDelaySeconds, useResendCountdown } from '../../lib';
import {
  getIsOrderConfirmationSessionExpired,
  getIsOrderConfirmationSessionValid,
  orderConfirmationSessionAtom,
  useConfirmOrder,
  useInitiateOrResendOrderConfirmation,
} from '../../model';

export type ConfirmOtpWidgetProps = {
  orderId: string;
  phoneNumber: string;
  onSuccess: () => void;
  submitButtonText?: string;
};

export function ConfirmOtpWidget({
  orderId,
  phoneNumber,
  onSuccess,
  submitButtonText = 'Confirm',
}: ConfirmOtpWidgetProps) {
  const orderSession = useAtomValue(orderConfirmationSessionAtom);

  const [otpValue, setOtpValue] = useState('');
  const [timer, startCountdown] = useResendCountdown();

  const confirmMutation = useConfirmOrder({
    onSuccess,
  });

  const initiateOrResendMutation = useInitiateOrResendOrderConfirmation({
    onSuccess: () => {
      setOtpValue('');
      confirmMutation.reset();
    },
  });

  useOnMountEffect(() => {
    const shouldInitiate =
      !orderSession ||
      !getIsOrderConfirmationSessionValid(orderSession, orderId);

    if (shouldInitiate) {
      initiateOrResendMutation.mutate({ orderId });
    }
  });

  useEffect(() => {
    startCountdown(getOtpRequestDelaySeconds(orderSession));
  }, [orderSession, startCountdown]);

  const handleOtpChange = useCallback((value: string) => {
    setOtpValue(value);
  }, []);

  const handleResend = useCallback(() => {
    initiateOrResendMutation.mutate({ orderId });
  }, [initiateOrResendMutation, orderId]);

  const handleSubmit = useCallback(() => {
    if (!orderSession) {
      return;
    }
    confirmMutation.mutate({
      sessionId: orderSession.sessionId,
      answer: { OTP: otpValue },
    });
  }, [confirmMutation, otpValue, orderSession]);

  const isOtpComplete = otpValue.length === 6;

  const isWrongOtp =
    confirmMutation.isError &&
    isWrongAnswerConfirmationError(confirmMutation.error);
  const isSessionExpired =
    !!orderSession && getIsOrderConfirmationSessionExpired(orderSession);

  const isSubmitDisabled =
    !isOtpComplete ||
    isWrongOtp ||
    initiateOrResendMutation.isPending ||
    confirmMutation.isPending;

  if (initiateOrResendMutation.isError) {
    return <OtpError />;
  }

  return (
    <div className="flex h-full w-full flex-col items-center gap-6">
      <div className="self-start pt-6">
        <Typography.Title size="m" className="mb-2">
          Enter&nbsp;the&nbsp;code from&nbsp;SMS
        </Typography.Title>
        <Typography.Text color="secondary">
          We sent a&nbsp;code to&nbsp;{formatPhone(phoneNumber)}
        </Typography.Text>
      </div>

      <div className="flex flex-col items-center gap-3">
        <InputOtp
          otpLength={6}
          value={otpValue}
          disabled={
            initiateOrResendMutation.isPending || confirmMutation.isPending
          }
          invalid={isWrongOtp}
          loading={confirmMutation.isPending}
          onChange={handleOtpChange}
        />
        {isWrongOtp && (
          <OtpErrorMessage message="Incorrect code entered. Please check the code and try again" />
        )}
        {isSessionExpired && (
          <OtpErrorMessage message="OTP we sent you has expired. Please request a new one." />
        )}
      </div>

      <ResendOtp
        timer={timer}
        isSendingOtp={initiateOrResendMutation.isPending}
        onResend={handleResend}
      />

      <Button
        type="button"
        size="l"
        width="full"
        loading={confirmMutation.isPending}
        disabled={isSubmitDisabled}
        onClick={handleSubmit}
        className="mt-auto"
      >
        {submitButtonText}
      </Button>
    </div>
  );
}
